// eslint-disable-next-line no-unused-vars
import React, { createElement, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { bool, object, array, func } from 'prop-types';
import { Grid, Row, Column } from '@carbon/react';
import {
  Laptop,
  Monitor,
  PcCase,
  WashingMachine,
  House,
  ShieldCheck,
  BriefcaseBusiness,
  Shapes,
  Volleyball,
  LampWallDown,
  Lamp,
  Thermometer,
  Microwave,
  HardHat,
  Armchair,
  GraduationCap,
  CreditCard,
  Circle,
  ChevronLeft,
  BellRing,
  Server,
} from 'lucide-react';

import useRouter from 'Hooks/useRouter';

const CategoriesContainer = styled.div`
  position: relative;
  display: flex;
  width: ${ props => props.categoryMenuOpen ? 'calc(100vw - 4rem)' : '260px' };
  box-shadow: 0 30px 65px ${ props => props.theme.colors.shadowGray };
  border-top: 1px solid ${ props => props.theme.colors.shadowGray };
  border-left: 1px solid ${ props => props.theme.colors.shadowGray };
  max-width: calc(1920px - 4em);
  padding: 0;
  background-color: ${ props => props.theme.colors.shadowGray };

  .menu {
    position: relative;
    max-width: 260px;

    > li {
      &.active:hover::before,
      &.active:hover::after {
        content: "";
        position: absolute;
        z-index: 8999;
        bottom: 0;
        height: 80%;
        animation: subnavHoverHelp 1.2s;
        opacity: .3;
      }

      &.active:hover::before {
        top: calc(-100% + 12px);
        clip-path: polygon(0% 100%, 100% 0%, 100% 100%);
      }
      &.active:hover::after {
        bottom: calc(-100% + 12px);
        clip-path: polygon(100% 0%, 0% 0%, 100% 100%);
      }

      &:hover, &.active {
        background-color: #EBEBEB;
        color: ${ props => props.theme.colors.altOrange };
        font-weight: 600;

        a svg {
          transform: rotate(180deg);
        }
      }

      color: #6A6767;

      display: flex;
      align-items: center;
      position: relative;
      padding: 10px 15px;
      width: 100%;
      border-bottom: 1px solid #DBDBDB;

      .iconContainer {
        display: flex;
        justify-content: center;
        width: 35px;
        margin-right: 8px;
      }

      a {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex: 1;
        gap: 8px;
        color: inherit;
        font-size: 0.875rem;
        line-height: 1.125rem;

        svg {
          min-width: 24px;
        }
      }
    }
  }

  .menuContainer {
    position: relative;
    bottom: -47px;
    margin-top: -47px;
    background-color: ${ props => props.theme.colors.white };
    width: ${ props => props.categoryMenuOpen ? '100%' : 'auto' };
    overflow: auto;
    border-right: 1px solid ${ props => props.theme.colors.shadowGray };
    border-bottom: 1px solid ${ props => props.theme.colors.shadowGray };
    z-index: 1;
  }
`;

const StyledGrid = styled(Grid)`
  width: 100%;
  display: flex;

  .title {
    &:after {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 27%;
      height: 2px;
      background-color: ${ props => props.theme.colors.default };
      content: '';
    }

    &:before {
      position: absolute;
      bottom: 0;
      width: 100%;
      height: 2px;
      background-color: ${ props => props.theme.products.colors.border };
      content: '';
    }

    display: -webkit-box;
    max-height: 40px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    position: relative;
    color: ${ props => props.theme.colors.darkGray };
    margin-bottom: 10px;
    padding-bottom: 10px;
    font-size: 0.875em;
    font-weight: 600;
    line-height: 1.25em;
    overflow: hidden;
    text-transform: uppercase;

    a {
      color: inherit;
    }
  }

  ul li {
    margin: 0.5rem 0;

    a {
      &:hover {
        font-weight: 600;
      }

      font-size: 0.875rem;
      color: ${ props => props.theme.colors.darkGray };
      line-height: 1rem
    }
  }

  .cds--row {
    flex-grow: 1;
  }

  .cds--css-grid-column {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  .active {
    font-weight: 600;
  }
`;

const CategoryIconMap = new Map();
CategoryIconMap.set('laptop-icon', Laptop);
CategoryIconMap.set('tv-icon', Monitor);
CategoryIconMap.set('computer-icon', PcCase);
CategoryIconMap.set('large-appliance-icon', WashingMachine);
CategoryIconMap.set('house-icon', House);
CategoryIconMap.set('security-icon', ShieldCheck);
CategoryIconMap.set('professional-solutions-icon', BriefcaseBusiness);
CategoryIconMap.set('kid-toy-icon', Shapes);
CategoryIconMap.set('basketball-icon', Volleyball);
CategoryIconMap.set('bulb-icon', LampWallDown);
CategoryIconMap.set('lamp-icon', Lamp);
CategoryIconMap.set('thermometer-icon', Thermometer);
CategoryIconMap.set('small-appliance-icon', Microwave);
CategoryIconMap.set('safety-helmet-icon', HardHat);
CategoryIconMap.set('furniture-icon', Armchair);
CategoryIconMap.set('education-icon', GraduationCap);
CategoryIconMap.set('pos-icon', CreditCard);
CategoryIconMap.set('bell-ring', BellRing);
CategoryIconMap.set('server', Server);
CategoryIconMap.set(null, Circle);

const CategoriesMenu = forwardRef((props, ref) => {
  const {
    categories,
    open,
    activeCategory,
    openCategory,
    onToggle,
    onOpenCategory,
  } = props;
  const { location, query } = useRouter();
  const sortOrder = query?.order_by ? `order_by=${query?.order_by}&order_direction=${query?.order_direction}` : '';
  const perPage = query?.count ? `count=${query?.count}` : '';
  let querySortPerPage = '';

  if (location.pathname.includes('/products') && query?.order_by && query?.count) {
    querySortPerPage = `?${perPage}&${sortOrder}`;
  } else if (location.pathname.includes('/products') && query?.order_by && query?.count) {
    querySortPerPage = `?${sortOrder}`;
  } else if (query?.count) {
    querySortPerPage = `?${perPage}`;
  }

  return (
    <CategoriesContainer
      className="CategoriesMenu grid-container"
      ref={ ref }
      onMouseLeave={ () => onToggle(false) }
      categoryMenuOpen={ openCategory }
      open={ open }
    >
      <nav className="menu">
        { categories.map(category => {
          const fn = () => onOpenCategory(category.id);
          const catIcon = CategoryIconMap.get(category.icon);
          return (
            <li
              key={ category.id }
              className={ openCategory?.id === category.id ? 'active' : '' }
              onMouseOver={ fn }
              onFocus={ fn }
            >
              { catIcon ? (
                <div className="iconContainer">
                  { createElement(catIcon) }
                </div>
              ) : null }
              <Link to={ `/products/${ category.slug }-c${ category.id }/c${querySortPerPage}` }>
                { category.name }
                <ChevronLeft />
              </Link>
            </li>
          );
        }) }
      </nav>
      <div className="menuContainer">
        { openCategory && (
          <StyledGrid key={ openCategory.id }>
            <Row>
              { openCategory
                .children
                .filter(childCategory => childCategory?.children?.length)
                .map(childCategory => (
                  <Column key={ childCategory.id }>
                    <h3 className="title">
                      <Link to={ `/products/${ childCategory.slug }-c${ childCategory.id }/c${querySortPerPage}` }>
                        { childCategory.name }
                      </Link>
                    </h3>
                    <ul>
                      { childCategory.children.map(cat => (
                        <li key={ cat.id }>
                          <Link
                            to={ `/products/${ cat.slug }-c${ cat.id }/c${querySortPerPage}` }
                            className={ activeCategory?.id === cat.id ? 'active' : '' }
                          >
                            { cat.name }
                          </Link>
                        </li>
                      )) }
                    </ul>
                  </Column>
                )) }
            </Row>
          </StyledGrid>
        ) }
      </div>
    </CategoriesContainer>
  );
});

CategoriesMenu.propTypes = {
  open: bool,
  categories: array,
  activeCategory: object,
  openCategory: object,
  onToggle: func,
  onOpenCategory: func,
};
CategoriesMenu.displayName = 'CategoriesMenuComponent';

export default CategoriesMenu;
